import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LogOut, Users, Plus, X, Search, KeyRound, UserX, Pencil, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { portalApi } from '@/lib/portalApi';
import { toast } from 'sonner';

/**
 * Phase 4 — Admin User Management page.
 *
 * List + filter by role/active + search; create users with a temp password
 * shown once to the admin; edit role/phone/name/active; reset password (one-tap
 * temp password); deactivate (soft delete preserves history).
 */
const RoleBadge = ({ role }) => {
  const styles = {
    admin:   'bg-red-700 text-white',
    tutor:   'bg-black text-[#D3D86F]',
    student: 'bg-teal-700 text-white',
    parent:  'bg-amber-500 text-white',
  }[role] || 'bg-gray-200 text-gray-800';
  return <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${styles}`}>{role}</span>;
};

const CreateUserModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', role: 'student', phone: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [createdPwd, setCreatedPwd] = useState(null);

  if (!open) return null;
  const reset = () => { setForm({ name: '', email: '', role: 'student', phone: '', password: '' }); setCreatedPwd(null); };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await portalApi.createUser({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        phone: form.phone.trim() || null,
        password: form.password.trim() || undefined,
      });
      toast.success('User created');
      setCreatedPwd(r.temp_password || null);
      onCreated(r);
      if (!r.temp_password) { reset(); onClose(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not create');
    } finally { setBusy(false); }
  };
  const closeMe = () => { reset(); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeMe} data-testid="admin-user-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-bold uppercase tracking-wider">{createdPwd ? 'User created' : 'New user'}</div>
          <button onClick={closeMe} className="h-7 w-7 rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="admin-user-modal-close"><X className="h-4 w-4" /></button>
        </div>
        {!createdPwd ? (
          <div className="px-5 py-4 space-y-3">
            <Field label="Full name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm" data-testid="admin-create-name" /></Field>
            <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm" data-testid="admin-create-email" /></Field>
            <Field label="Role">
              <div className="grid grid-cols-4 gap-1.5">
                {['student', 'tutor', 'parent', 'staff'].map((r) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, role: r })}
                    className={`h-9 rounded-lg text-[10px] uppercase tracking-wider font-bold ${form.role === r ? 'bg-red-700 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                    data-testid={`admin-create-role-${r}`}>{r}</button>
                ))}
              </div>
            </Field>
            <div className="text-[10px] text-gray-500 italic -mt-1">Admin / Owner accounts are seeded manually via the backend — not from this UI.</div>
            <Field label="Phone (optional)"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm" data-testid="admin-create-phone" /></Field>
            <Field label="Initial password (optional — leave blank to auto-generate)"><input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="8+ chars" className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm" data-testid="admin-create-password" /></Field>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-3">
            <div className="text-xs text-gray-600">A temporary password was generated. Copy it now — it won't be shown again.</div>
            <div className="bg-black text-[#D3D86F] rounded-xl p-4 font-mono text-sm break-all" data-testid="admin-create-temp-pwd">{createdPwd}</div>
            <div className="text-[11px] text-gray-500">Share securely with the user. They can change it from their portal once logged in.</div>
          </div>
        )}
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
          {!createdPwd ? (
            <>
              <button onClick={closeMe} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider" data-testid="admin-create-cancel">Cancel</button>
              <button onClick={submit} disabled={busy || !form.name.trim() || !form.email.trim()}
                className="h-9 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5"
                data-testid="admin-create-submit">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Create
              </button>
            </>
          ) : (
            <button onClick={closeMe} className="h-9 px-5 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider" data-testid="admin-create-done">Done</button>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">{label}</div>
    {children}
  </label>
);

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ role: '', q: '' });
  const [loadingData, setLoadingData] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user obj

  const refresh = useCallback(async () => {
    setLoadingData(true);
    try {
      const r = await portalApi.listUsers({ role: filter.role || undefined, q: filter.q || undefined });
      setItems(r.items || []);
    } catch (_e) {} finally { setLoadingData(false); }
  }, [filter]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/admin/login', { replace: true }); return; }
    if (!['admin', 'owner'].includes((user.role || '').toLowerCase())) { navigate('/', { replace: true }); return; }
    refresh();
  }, [loading, user, navigate, refresh]);

  const resetPwd = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?`)) return;
    try {
      const r = await portalApi.resetPassword(u.id);
      if (r.temp_password) {
        window.prompt('New temporary password (copy now):', r.temp_password);
      } else {
        toast.success('Password reset');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };
  const toggleActive = async (u) => {
    try {
      if (u.active === false) {
        await portalApi.updateUser(u.id, { active: true });
      } else {
        await portalApi.deactivate(u.id);
      }
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-users-page">
      <header className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-[#D3D86F]" />
            <div>
              <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold">WINHOUSE ADMIN</div>
              <div className="text-sm font-bold">User management</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/admin/classes')}
              className="h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider hidden sm:flex items-center gap-1.5"
              data-testid="admin-nav-classes">Classes</button>
            <button onClick={() => { logout(); navigate('/admin/login'); }}
              className="h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="portal-logout"><LogOut className="h-3 w-3" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 max-w-md bg-white border border-gray-200 rounded-full px-4 h-10">
            <Search className="h-4 w-4 text-gray-400" />
            <input placeholder="Search by name or email…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="admin-users-search"
              onKeyDown={(e) => { if (e.key === 'Enter') refresh(); }} />
          </div>
          <div className="flex items-center gap-1.5">
            {['', 'student', 'tutor', 'parent', 'staff', 'admin'].map((r) => (
              <button key={r || 'all'} onClick={() => setFilter({ ...filter, role: r })}
                className={`h-9 px-3 rounded-full text-[10px] font-bold uppercase tracking-wider ${filter.role === r ? 'bg-black text-white' : 'bg-white border border-gray-200 hover:bg-gray-100'}`}
                data-testid={`admin-users-filter-${r || 'all'}`}>{r || 'all'}</button>
            ))}
          </div>
          <button onClick={() => setCreateOpen(true)}
            className="h-10 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
            data-testid="admin-users-create">
            <Plus className="h-3 w-3" /> New user
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {loadingData ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>
          ) : items.length === 0 ? (
            <div className="text-center text-xs text-gray-400 italic py-10" data-testid="admin-users-empty">No users match this filter.</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{u.name || '—'}</td>
                    <td className="px-4 py-3 text-gray-700 font-mono text-[12px]">{u.email}</td>
                    <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${u.active === false ? 'bg-gray-200 text-gray-600' : 'bg-teal-700 text-white'}`}>
                        {u.active === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setEditing(u)} className="h-8 w-8 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center" title="Edit" data-testid={`admin-edit-${u.id}`}><Pencil className="h-3 w-3" /></button>
                        <button onClick={() => resetPwd(u)} className="h-8 w-8 rounded-lg border border-gray-200 hover:bg-amber-50 flex items-center justify-center text-amber-700" title="Reset password" data-testid={`admin-reset-${u.id}`}><KeyRound className="h-3 w-3" /></button>
                        <button onClick={() => toggleActive(u)} className="h-8 w-8 rounded-lg border border-gray-200 hover:bg-red-50 flex items-center justify-center text-red-700" title={u.active === false ? 'Activate' : 'Deactivate'} data-testid={`admin-toggle-active-${u.id}`}><UserX className="h-3 w-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(r) => { setItems((it) => [r, ...it]); }} />

      {editing && (
        <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
};

const EditUserModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: user.name || '', role: user.role, phone: user.phone || '', active: user.active !== false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await portalApi.updateUser(user.id, form);
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="admin-edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-bold uppercase tracking-wider">Edit user</div>
          <button onClick={onClose} className="h-7 w-7 rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="admin-edit-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-[11px] text-gray-500 font-mono">{user.email}</div>
          <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm" data-testid="admin-edit-name" /></Field>
          <Field label="Role">
            <div className="grid grid-cols-4 gap-1.5">
              {(['admin','owner'].includes((user.role||'').toLowerCase()) ? ['student','tutor','parent','staff','admin','owner'] : ['student','tutor','parent','staff']).map((r) => {
                const isProtectedRole = ['admin','owner'].includes(r);
                const isProtectedCurrent = ['admin','owner'].includes((user.role||'').toLowerCase());
                const disabled = isProtectedRole || isProtectedCurrent;
                return (
                  <button key={r} type="button" disabled={disabled} onClick={() => !disabled && setForm({ ...form, role: r })}
                    className={`h-9 rounded-lg text-[10px] uppercase tracking-wider font-bold ${form.role === r ? 'bg-red-700 text-white' : 'bg-gray-100 hover:bg-gray-200'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    data-testid={`admin-edit-role-${r}`}>{r}</button>
                );
              })}
            </div>
            {['admin','owner'].includes((user.role||'').toLowerCase()) && (
              <div className="text-[10px] text-amber-700 mt-1 italic">Admin/Owner role changes are protected — edit directly via the backend if needed.</div>
            )}
          </Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm" data-testid="admin-edit-phone" /></Field>
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-red-700" data-testid="admin-edit-active" />
            <span className="text-xs">Active</span>
          </label>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider" data-testid="admin-edit-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="h-9 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40" data-testid="admin-edit-save">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
